import { AnimatePresence, motion } from 'framer-motion'
import { Plus } from 'lucide-react'
import React from 'react'
import { Button } from 'vnetwork-ui'
import { isStageActive } from './career-path-constants'
import { useCareerPath } from './use-career-path'
import ActiveStageCard from './ActiveStageCard'
import CompletedStageCard from './CompletedStageCard'
import ExtendPeriodDialog from './ExtendPeriodDialog'
import NoteDialog from './NoteDialog'
import StageDialog from './StageDialog'
import StageProgressionBar from './StageProgressionBar'
import TransitionDialog from './TransitionDialog'

interface CareerPathProps {
    employeeId?: number
    isSelf?: boolean
}

const CareerPath: React.FC<CareerPathProps> = ({
    employeeId,
    isSelf = false,
}) => {
    const {
        sortedPaths,
        latestStage,
        passedStages,
        stages,
        hasStages,
        renderState,
        t,
        // UI state
        expandedPaths,
        showStageDialog,
        editingStage,
        noteDialogPathId,
        transitionDialog,
        extendDialog,
        // Setters
        setShowStageDialog,
        setEditingStage,
        setNoteDialogPathId,
        setTransitionDialog,
        setExtendDialog,
        toggleExpand,
        // Handlers
        handleCreateOrUpdateStage,
        handleTransition,
        handleCreateNote,
        handleExtendPeriod,
        // Loading
        isCreating,
        isUpdating,
        isTransitioning,
        isCreatingNote,
        isExtending,
    } = useCareerPath(employeeId, isSelf)

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-4">
            {/* Start Career button (when no stages) */}
            {!isSelf && !hasStages && (
                <motion.div
                    className="flex justify-end"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setEditingStage(null)
                            setShowStageDialog(true)
                        }}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('career_path.start_career')}
                    </Button>
                </motion.div>
            )}

            {/* Stage Progression Bar */}
            {hasStages && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                >
                    <StageProgressionBar
                        currentStage={latestStage}
                        passedStages={passedStages}
                        stages={stages}
                    />
                </motion.div>
            )}

            {/* Empty state */}
            <AnimatePresence>
                {!hasStages && (
                    <motion.div
                        className="text-center py-12 text-gray-500"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        {t('career_path.no_data')}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Timeline */}
            <div className="relative">
                {/* SVG vertical line */}
                {hasStages && (
                    <svg
                        className="absolute left-[12px] top-0 h-full w-[8px] z-0"
                        preserveAspectRatio="none"
                    >
                        <defs>
                            <linearGradient
                                id="timeline-gradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="#3b82f6"
                                    stopOpacity="0.2"
                                />
                                <stop
                                    offset="50%"
                                    stopColor="#22c55e"
                                    stopOpacity="0.4"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="#9ca3af"
                                    stopOpacity="0.2"
                                />
                            </linearGradient>
                        </defs>
                        <motion.rect
                            x="3"
                            y="0"
                            width="2"
                            rx="1"
                            fill="url(#timeline-gradient)"
                            initial={{ height: 0 }}
                            animate={{ height: '100%' }}
                            transition={{
                                duration: 0.6,
                                ease: 'easeOut',
                            }}
                        />
                    </svg>
                )}

                <AnimatePresence mode="popLayout">
                    <div className="space-y-4">
                        {sortedPaths.map((path, index) => {
                            const isFirst = index === 0
                            const isActive =
                                isFirst &&
                                isStageActive(path.career_path_end_date)
                            const stageInfo = stages.find(
                                (s) => s.value === path.career_path_stage
                            )

                            if (isActive) {
                                return (
                                    <ActiveStageCard
                                        key={path.career_path_id}
                                        path={path}
                                        index={index}
                                        stageInfo={stageInfo}
                                        isSelf={isSelf}
                                        onTransition={(action) =>
                                            setTransitionDialog({
                                                action,
                                                currentPath: path,
                                            })
                                        }
                                        onExtend={() => setExtendDialog(path)}
                                        onAddNote={() =>
                                            setNoteDialogPathId(
                                                path.career_path_id
                                            )
                                        }
                                        onEdit={() => {
                                            setEditingStage(path)
                                            setShowStageDialog(true)
                                        }}
                                        t={t}
                                    />
                                )
                            }

                            return (
                                <CompletedStageCard
                                    key={path.career_path_id}
                                    path={path}
                                    index={index}
                                    stageInfo={stageInfo}
                                    isSelf={isSelf}
                                    isExpanded={expandedPaths.has(
                                        path.career_path_id
                                    )}
                                    onToggleExpand={() =>
                                        toggleExpand(path.career_path_id)
                                    }
                                    onEdit={() => {
                                        setEditingStage(path)
                                        setShowStageDialog(true)
                                    }}
                                    onAddNote={() =>
                                        setNoteDialogPathId(path.career_path_id)
                                    }
                                    t={t}
                                />
                            )
                        })}
                    </div>
                </AnimatePresence>
            </div>

            {/* Dialogs */}
            <StageDialog
                open={showStageDialog}
                onClose={() => {
                    setShowStageDialog(false)
                    setEditingStage(null)
                }}
                editingStage={editingStage}
                stages={stages}
                isLoading={isCreating || isUpdating}
                onSubmit={handleCreateOrUpdateStage}
                t={t}
            />

            <TransitionDialog
                open={transitionDialog !== null}
                onClose={() => setTransitionDialog(null)}
                action={transitionDialog?.action || null}
                currentPath={transitionDialog?.currentPath || null}
                isLoading={isTransitioning}
                onSubmit={handleTransition}
                t={t}
            />

            <NoteDialog
                open={noteDialogPathId !== null}
                onClose={() => setNoteDialogPathId(null)}
                isLoading={isCreatingNote}
                onSubmit={handleCreateNote}
                t={t}
            />

            <ExtendPeriodDialog
                open={extendDialog !== null}
                onClose={() => setExtendDialog(null)}
                currentPath={extendDialog}
                stages={stages}
                isLoading={isExtending}
                onSubmit={handleExtendPeriod}
                t={t}
            />
        </div>
    )
}

export default CareerPath
